import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Check } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_COLORS = {
  easy: "#71c4ff", long: "#d7ff00", threshold: "#ff8800", tempo: "#ff8800",
  vo2: "#ff3b30", hill: "#ff6b00", recovery: "#7fffd4", strength: "#c084fc",
  mobility: "#a3e635", rest: "#444", race: "#d7ff00",
};

const FEEL_COLOR = {
  great: "#d7ff00", good: "#7fffd4", average: "#ffb800",
  tired: "#ff8800", very_tired: "#ff6b00", pain: "#ff3b30",
};

function Stat({ label, value }) {
  return (
    <div className="bg-[#0e0e0e] border border-[#1f1f1f] px-3 py-2">
      <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#666]">{label}</div>
      <div className="font-display text-lg text-white mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

/**
 * Read-only program detail. Fetches from `endpoint` (athlete or admin variant).
 * Shows per-program stats + the full schedule grouped by week, with completed
 * workout feedback (feel, RPE, distance, duration, pace, splits, notes).
 */
export default function ProgramDetailModal({ endpoint, title, status, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(endpoint).then(r => { if (alive) setData(r.data); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [endpoint]);

  const sessions = data?.program?.sessions || [];
  const weeks = {};
  sessions.forEach(s => {
    const w = s.week_index ?? 0;
    (weeks[w] = weeks[w] || []).push(s);
  });
  const weekNums = Object.keys(weeks).map(Number).sort((a, b) => a - b);
  const st = data?.stats || {};

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="program-detail-modal">
      <div className="bg-[#0a0a0a] border border-[#d7ff00]/40 w-full max-w-3xl my-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-[#1f1f1f] p-4 flex items-start justify-between z-10">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-2">
              {status === "current" ? "CURRENT PROGRAM" : "COMPLETED · READ-ONLY"}
            </div>
            <h3 className="font-display text-2xl uppercase mt-1">{data?.program?.name || title || "Program"}</h3>
          </div>
          <button onClick={onClose} className="text-[#888] hover:text-white" data-testid="program-detail-close"><X className="w-5 h-5" /></button>
        </div>

        {err && <div className="p-6 text-[#ff6b6b] text-sm">Could not load this program.</div>}
        {!data && !err && <div className="p-6 text-[#888] font-mono-jp text-xs uppercase tracking-widest">Loading…</div>}

        {data && (
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="program-detail-stats">
              <Stat label="Compliance" value={st.compliance_pct != null ? `${st.compliance_pct}%` : "—"} />
              <Stat label="Distance" value={st.total_distance_km != null ? `${st.total_distance_km} km` : "—"} />
              <Stat label="Avg RPE" value={st.avg_rpe} />
              <Stat label="Sessions" value={`${st.completed_sessions}/${st.total_sessions}`} />
              <Stat label="Missed" value={st.missed_sessions} />
              <Stat label="Longest run" value={st.longest_run_km != null ? `${st.longest_run_km} km` : "—"} />
              <Stat label="Fastest pace" value={st.fastest_pace} />
              <Stat label="Training load" value={st.training_load} />
            </div>

            <div className="mt-5 space-y-5">
              {weekNums.map(wn => (
                <div key={wn}>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mb-2">Week {wn + 1}</div>
                  <div className="space-y-2">
                    {weeks[wn].sort((a, b) => (a.day_index ?? 0) - (b.day_index ?? 0)).map((s, i) => {
                      const color = TYPE_COLORS[s.type] || "#d7ff00";
                      const fb = s.feedback;
                      return (
                        <div key={`${wn}-${s.day_index}-${i}`} className={`bg-[#0e0e0e] border ${s.completed ? "border-[#d7ff00]/30" : "border-[#1a1a1a]"} p-3`} data-testid={`detail-session-${wn}-${s.day_index}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono-jp text-[9px] text-[#666] w-8">{DAYS[s.day_index ?? 0]}</span>
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                            <span className="font-display text-sm uppercase">{s.name}</span>
                            {s.distance_km ? <span className="font-mono-jp text-[9px] text-[#bbb] border border-[#222] px-1.5">{s.distance_km} KM</span> : null}
                            {s.pace_target ? <span className="font-mono-jp text-[9px] text-[#bbb] border border-[#222] px-1.5">{s.pace_target}</span> : null}
                            {s.completed && <Check className="w-3.5 h-3.5 text-[#d7ff00] ml-auto" />}
                          </div>
                          {fb && (
                            <div className="mt-2 pl-10 text-xs text-[#ddd] space-y-1">
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {fb.feel && <span>Feel: <span style={{ color: FEEL_COLOR[fb.feel] || "#fff" }}>{fb.feel.replace("_", " ")}</span></span>}
                                {fb.rpe != null && <span>RPE {fb.rpe}</span>}
                                {fb.actual_distance_km != null && <span>{fb.actual_distance_km} km</span>}
                                {fb.actual_duration_min != null && <span>{fb.actual_duration_min} min</span>}
                                {fb.actual_pace && <span>@ {fb.actual_pace}</span>}
                              </div>
                              {fb.intervals && <div className="text-[#bbb]"><span className="text-[#666]">Splits:</span> {fb.intervals}</div>}
                              {fb.notes && <div className="text-[#bbb]"><span className="text-[#666]">Notes:</span> {fb.notes}</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
